// Google Analytics 4 integration with consent management
// Only loads after user explicitly consents to analytics cookies

const GA_MEASUREMENT_ID = 'G-QGM9CQ9BT1';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Initialize Google Analytics (should only be called after user consent)
 */
export function initializeGA(): void {
  // Check if already initialized
  if (window.gtag) {
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };

  // Configure initial timestamp
  window.gtag('js', new Date());

  // Configure GA with basic settings
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=Lax;Secure',
  });

  // Load GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  console.log('[Analytics] Google Analytics initialized');
}

/**
 * Track custom event in Google Analytics
 */
export function trackEvent(eventName: string, eventParams?: Record<string, unknown>): void {
  if (!window.gtag) {
    console.warn('[Analytics] GA not initialized, event not tracked:', eventName);
    return;
  }

  window.gtag('event', eventName, eventParams);
  console.log('[Analytics] Event tracked:', eventName, eventParams);
}

/**
 * Track page view in Google Analytics
 */
export function trackPageView(page_path: string, page_title?: string): void {
  if (!window.gtag) {
    console.warn('[Analytics] GA not initialized, page view not tracked:', page_path);
    return;
  }

  window.gtag('event', 'page_view', {
    page_path,
    page_title,
  });
  console.log('[Analytics] Page view tracked:', page_path);
}

/**
 * Update Google Consent Mode (v2)
 */
export function updateConsent(granted: boolean): void {
  if (!window.gtag) {
    return;
  }

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied', // We don't use ads
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  console.log('[Analytics] Consent updated:', granted ? 'granted' : 'denied');
}

/**
 * Set default consent state (called before GA loads)
 */
export function setDefaultConsent(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };

  // Set default consent to denied (GDPR-compliant)
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500, // Wait 500ms for consent dialog
  });

  console.log('[Analytics] Default consent set to denied');
}

/**
 * Track chat message sent
 */
export function trackChatMessage(conversationId: string, isNewConversation: boolean): void {
  trackEvent('chat_message_sent', {
    conversation_id: conversationId,
    is_new_conversation: isNewConversation,
  });
}

/**
 * Track conversation created
 */
export function trackConversationCreated(conversationId: string): void {
  trackEvent('conversation_created', {
    conversation_id: conversationId,
  });
}

/**
 * Track MCP tool used (based on tool call message)
 */
export function trackToolUsed(toolName: string): void {
  trackEvent('mcp_tool_used', {
    tool_name: toolName,
  });
}

/**
 * Track user login
 */
export function trackLogin(method: 'google' | 'facebook'): void {
  trackEvent('login', {
    method,
  });
}

/**
 * Track user signup
 */
export function trackSignup(method: 'google' | 'facebook'): void {
  trackEvent('sign_up', {
    method,
  });
}
